/**
 * Sound Manager for Phaser Games
 * 
 * Features:
 * - Background music with crossfade
 * - Sound effects with category management
 * - Volume control for music and effects (both global and per category)
 * - Automatic audio pool management to avoid performance issues
 * - Persistence of volume settings in localStorage
 */
export default class SoundManager {
  /**
   * @param {Phaser.Scene} scene - The scene this sound manager belongs to
   * @param {Object} options - Configuration options
   * @param {boolean} [options.mute=false] - Whether to start muted
   * @param {number} [options.musicVolume=0.5] - Initial music volume (0-1)
   * @param {number} [options.effectsVolume=0.7] - Initial sound effects volume (0-1)
   * @param {string} [options.storageKey='phaser-audio-settings'] - LocalStorage key for saving settings
   * @param {number} [options.fadeTime=1000] - Default fade time in ms
   */
  constructor(scene, options = {}) {
    this.scene = scene;
    this.options = {
      mute: false,
      musicVolume: 0.5,
      effectsVolume: 0.7,
      storageKey: 'phaser-audio-settings',
      fadeTime: 1000,
      ...options
    };

    // Volume controls
    this.masterVolume = 1;
    this.musicVolume = this.options.musicVolume;
    this.effectsVolume = this.options.effectsVolume;

    // Track all sounds
    this.music = null;
    this.previousMusic = null;
    this.effects = new Map();
    this.effectCategories = new Map();

    // Load saved settings
    this.loadSettings();

    // Register shutdown event to stop all sounds when scene ends
    //this.scene.events.once('shutdown', this.shutdown, this);
  }

  /**
   * Load audio settings from localStorage
   * @private
   */
  loadSettings() {
    try {
      const savedSettings = localStorage.getItem(this.options.storageKey);
      this.muteSettings = false;
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        this.muteSettings = settings.mute;
        if (settings.mute) {
          this.mute();
        }
      }
    } catch (error) {
      console.warn('Failed to load audio settings:', error);
    }
  }

  /**
   * Save current audio settings to localStorage
   * @private
   */
  saveSettings() {
    try {
      const settings = {
        mute: this.options.mute
      };

      localStorage.setItem(this.options.storageKey, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save audio settings:', error);
    }
  }

  /**
   * Play background music with optional crossfade
   * @param {string} key - Asset key for the music to play
   * @param {Object} [options] - Options for playback
   * @param {boolean} [options.loop=true] - Whether the music should loop
   * @param {number} [options.volume=1] - Volume for this specific track (0-1)
   * @param {number} [options.fadeIn] - Fade in time in ms
   * @param {number} [options.fadeOut] - Fade out time for previous track in ms
   * @param {boolean} [options.stopPrevious=true] - Whether to stop previous music
   * @returns {Phaser.Sound.BaseSound} The music sound object
   */
  playMusic(key, options = {}) {
    const defaultOptions = {
      loop: true,
      volume: 1,
      fadeIn: this.options.fadeTime,
      fadeOut: this.options.fadeTime,
      stopPrevious: true
    };

    const config = { ...defaultOptions, ...options };

    // If we're already playing this music, don't restart it
    if (this.music && this.music.key === key && this.music.isPlaying) {
      return this.music;
    }

    // Handle previous music
    if (this.music && config.stopPrevious) {
      this.previousMusic = this.music;

      if (config.fadeOut > 0) {
        this.scene.tweens.add({
          targets: this.previousMusic,
          volume: 0,
          duration: config.fadeOut,
          onComplete: () => {
            this.previousMusic.stop();
            this.previousMusic = null;
          }
        });
      } else {
        this.previousMusic.stop();
        this.previousMusic = null;
      }
    }

    // Create and play new music
    const actualVolume = config.volume * this.musicVolume * this.masterVolume;
    const startVolume = config.fadeIn > 0 ? 0 : actualVolume;

    this.music = this.scene.sound.add(key, {
      loop: config.loop,
      volume: startVolume
    });

    this.music.play();

    // Apply fade in if needed
    if (config.fadeIn > 0) {
      this.scene.tweens.add({
        targets: this.music,
        volume: actualVolume,
        duration: config.fadeIn
      });
    }

    return this.music;
  }

  /**
   * Stop the currently playing music
   * @param {Object} [options] - Options for stopping
   * @param {number} [options.fadeOut] - Fade out time in ms
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  stopMusic(options = {}) {
    const defaultOptions = {
      fadeOut: this.options.fadeTime
    };

    const config = { ...defaultOptions, ...options };

    if (this.music) {
      if (config.fadeOut > 0) {
        this.scene.tweens.add({
          targets: this.music,
          volume: 0,
          duration: config.fadeOut,
          onComplete: () => {
            this.music.stop();
            this.music = null;
          }
        });
      } else {
        this.music.stop();
        this.music = null;
      }
    }

    return this;
  }

  /**
   * Pause the currently playing music
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  pauseMusic() {
    if (this.music && this.music.isPlaying) {
      this.music.pause();
    }
    return this;
  }

  /**
   * Resume the currently paused music
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  resumeMusic() {
    if (this.music && this.music.isPaused) {
      this.music.resume();
    }
    return this;
  }

  /**
   * Register a sound effect category with its own volume setting
   * @param {string} category - Name of the category
   * @param {number} [volume=1] - Volume multiplier for this category (0-1)
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  addCategory(category, volume = 1) {
    this.effectCategories.set(category, volume);
    return this;
  }

  /**
   * Set volume for a specific sound effect category
   * @param {string} category - Name of the category
   * @param {number} volume - Volume multiplier for this category (0-1)
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  setCategoryVolume(category, volume) {
    if (this.effectCategories.has(category)) {
      this.effectCategories.set(category, volume);

      // Update volume for any playing sounds in this category
      this.effects.forEach((sound, key) => {
        if (key.startsWith(category + ':')) {
          sound.setVolume(volume * this.effectsVolume * this.masterVolume);
        }
      });
    } else {
      console.warn(`Sound category '${category}' doesn't exist.`);
    }

    return this;
  }

  /**
   * Play a sound effect
   * @param {string} key - Asset key for the sound to play
   * @param {Object} [options] - Options for playback
   * @param {string} [options.category='default'] - Sound category for volume control
   * @param {number} [options.volume=1] - Volume for this specific sound (0-1)
   * @param {boolean} [options.loop=false] - Whether the sound should loop
   * @param {number} [options.rate=1] - Playback rate (1 = normal, 0.5 = half speed, 2 = double speed)
   * @param {boolean} [options.pool=false] - If true, limit number of concurrent instances of this sound
   * @param {number} [options.poolSize=3] - Maximum number of concurrent instances when pool is enabled
   * @returns {Phaser.Sound.BaseSound} The sound effect object
   */
  playEffect(key, options = {}) {
    const defaultOptions = {
      category: 'default',
      volume: 1,
      loop: false,
      rate: 1,
      pool: false,
      poolSize: 3
    };

    const config = { ...defaultOptions, ...options };

    // Make sure the category exists
    if (!this.effectCategories.has(config.category)) {
      this.addCategory(config.category);
    }

    // Check sound pool limits
    const poolKey = `${config.category}:${key}`;
    let activeSounds = 0;

    if (config.pool) {
      this.effects.forEach((sound, soundKey) => {
        if (soundKey.includes(poolKey) && sound.isPlaying) {
          activeSounds++;
        }
      });

      if (activeSounds >= config.poolSize) {
        // Don't play if pool is full
        return null;
      }
    }

    // Calculate final volume
    const categoryVolume = this.effectCategories.get(config.category);
    const finalVolume = config.volume * categoryVolume * this.effectsVolume * this.masterVolume;

    // Create unique key for this sound instance
    const instanceKey = `${poolKey}_${Date.now()}`;

    // Create and play sound
    const sound = this.scene.sound.add(key, {
      loop: config.loop,
      volume: finalVolume,
      rate: config.rate
    });

    sound.play();

    // Store reference for management
    this.effects.set(instanceKey, sound);

    // Auto cleanup when sound completes
    sound.once('complete', () => {
      sound.destroy();
      this.effects.delete(instanceKey);
    });

    return sound;
  }

  /**
   * Stop all sound effects, optionally filtered by category
   * @param {string} [category] - If provided, only stop sounds in this category
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  stopEffects(category) {
    this.effects.forEach((sound, key) => {
      if (!category || key.startsWith(category + ':')) {
        sound.stop();
        sound.destroy();
        this.effects.delete(key);
      }
    });

    return this;
  }

  /**
   * Set the master volume that affects all sounds
   * @param {number} volume - Master volume (0-1)
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  setMasterVolume(volume) {
    this.masterVolume = Phaser.Math.Clamp(volume, 0, 1);

    // Update music volume
    if (this.music) {
      this.music.setVolume(this.musicVolume * this.masterVolume);
    }

    // Update effect volumes
    this.effects.forEach((sound, key) => {
      const category = key.split(':')[0];
      const categoryVolume = this.effectCategories.get(category) || 1;
      sound.setVolume(sound.volumeMultiplier * categoryVolume * this.effectsVolume * this.masterVolume);
    });

    this.saveSettings();
    return this;
  }

  /**
   * Set the volume level for all music
   * @param {number} volume - Music volume (0-1)
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  setMusicVolume(volume) {
    this.musicVolume = Phaser.Math.Clamp(volume, 0, 1);

    if (this.music) {
      this.music.setVolume(this.musicVolume * this.masterVolume);
    }

    this.saveSettings();
    return this;
  }

  /**
   * Set the volume level for all sound effects
   * @param {number} volume - Effects volume (0-1)
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  setEffectsVolume(volume) {
    this.effectsVolume = Phaser.Math.Clamp(volume, 0, 1);

    // Update all effects
    this.effects.forEach((sound, key) => {
      const category = key.split(':')[0];
      const categoryVolume = this.effectCategories.get(category) || 1;
      sound.setVolume(sound.volumeMultiplier * categoryVolume * this.effectsVolume * this.masterVolume);
    });

    this.saveSettings();
    return this;
  }

  /**
   * Mute all audio
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  mute() {
    this.options.mute = true;
    this.scene.sound.setMute(true);
    this.saveSettings();
    return this;
  }

  /**
   * Unmute all audio
   * @returns {SoundManager} This SoundManager instance for chaining
   */
  unmute() {
    this.options.mute = false;
    this.scene.sound.setMute(false);
    this.saveSettings();
    return this;
  }

  /**
   * Toggle mute state
   * @returns {boolean} New mute state
   */
  toggleMute() {
    this.options.mute = !this.options.mute;
    this.scene.sound.setMute(this.options.mute);
    this.saveSettings();
    return this.options.mute;
  }

  /**
   * Clean up and stop all sounds when scene shuts down
   * @private
   */
  shutdown() {
    if (this.music) {
      this.music.stop();
      this.music = null;
    }

    if (this.previousMusic) {
      this.previousMusic.stop();
      this.previousMusic = null;
    }

    // Stop all effects
    this.stopEffects();

    // Clean up event listeners
    this.scene.events.off('shutdown', this.shutdown, this);
  }
}